import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Images, PlaySquare, CheckSquare, Square, ArrowLeft, Folder } from "lucide-react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { SampleTile, SampleViewer } from "@/components/SampleViewer";
import { FolderCard } from "@/components/FolderCard";
import { Button } from "@/components/ui/button";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Samples() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState({});
  const [showing, setShowing] = useState(null); // array of folder ids being displayed
  const [viewer, setViewer] = useState(null); // { items, index }

  const fromBuilder = !!location.state?.fromBuilder;
  const leave = () => (fromBuilder || location.key !== "default" ? navigate(-1) : navigate("/"));
  const back = () => (showing ? setShowing(null) : leave());

  useEffect(() => {
    api
      .get(`/products/${productId}/samples`)
      .then((r) => setData(r.data))
      .catch(() => setError("We couldn't find samples for this item."));
  }, [productId]);

  const folders = useMemo(() => (data?.folders || []).filter((f) => f.count > 0), [data]);
  const samples = data?.samples || [];
  const selected = useMemo(() => folders.filter((f) => checked[f.id]), [folders, checked]);
  const allChecked = folders.length > 0 && selected.length === folders.length;
  const toggleAll = () => setChecked(allChecked ? {} : Object.fromEntries(folders.map((f) => [f.id, true])));

  const shownFolders = useMemo(() => (showing ? folders.filter((f) => showing.includes(f.id)) : []), [folders, showing]);
  const shownItems = useMemo(() => shownFolders.flatMap((f) => samples.filter((s) => s.folder_id === f.id)), [shownFolders, samples]);

  return (
    <div className="min-h-screen">
      <Header onBack={back} />
      <main className="mx-auto max-w-6xl px-5 py-10" data-testid="samples-page">
        <button type="button" onClick={leave} data-testid="samples-back-to-build" className="mb-4 inline-flex items-center gap-1.5 text-sm font-bold text-[#1B66C9] hover:underline">
          <ArrowLeft size={15} /> Back to your build
        </button>
        {error && <p className="py-20 text-center text-muted-foreground" data-testid="samples-error">{error}</p>}
        {data && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div className="flex items-center gap-4">
                {data.product.image_url && (
                  <img src={data.product.image_url} alt="" className="hidden h-20 w-28 rounded-md object-cover sm:block" />
                )}
                <div>
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1B66C9]">Samples</p>
                  <h1 className="font-display text-3xl font-black tracking-tight sm:text-4xl" data-testid="samples-product-name">
                    {data.product.name}
                  </h1>
                  <p className="text-sm text-muted-foreground">
                    {[data.product.brand, data.product.series].filter(Boolean).join(" · ")}
                    {folders.length > 0 && ` · ${folders.length} folder${folders.length === 1 ? "" : "s"} · ${samples.length} item${samples.length === 1 ? "" : "s"}`}
                  </p>
                </div>
              </div>
              {!showing && folders.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" data-testid="folders-select-all" onClick={toggleAll} className="font-bold">
                    {allChecked ? <CheckSquare size={16} className="mr-1.5" /> : <Square size={16} className="mr-1.5" />}
                    {allChecked ? "Clear selection" : "Select all"}
                  </Button>
                  <Button
                    data-testid="folders-view-selected"
                    disabled={selected.length === 0}
                    onClick={() => setShowing(selected.map((f) => f.id))}
                    className="rounded-md bg-[#1B66C9] font-bold hover:bg-[#144E9C]"
                  >
                    <PlaySquare size={16} className="mr-1.5" /> View selected ({selected.length})
                  </Button>
                </div>
              )}
              {showing && (
                <Button variant="outline" data-testid="folders-back" onClick={() => setShowing(null)} className="font-bold">
                  <ArrowLeft size={16} className="mr-1.5" /> Back to folders
                </Button>
              )}
            </div>

            {folders.length === 0 ? (
              <div className="mt-16 flex flex-col items-center text-center text-muted-foreground" data-testid="samples-empty">
                <Images size={40} className="mb-3" />
                <p className="font-semibold">Samples are on the way.</p>
                <p className="text-sm">Check back soon or give us a call at 386-747-7652.</p>
              </div>
            ) : !showing ? (
              <>
                <p className="mt-6 text-sm text-muted-foreground" data-testid="folders-hint">
                  Tap a folder to open it, or tick the ones you want and view them together.
                </p>
                <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="folders-grid">
                  {folders.map((f) => (
                    <FolderCard
                      key={f.id}
                      folder={f}
                      testId={`folder-${slug(f.name)}`}
                      checked={!!checked[f.id]}
                      onCheck={() => setChecked((c) => ({ ...c, [f.id]: !c[f.id] }))}
                      onOpen={() => setShowing([f.id])}
                    />
                  ))}
                </div>
              </>
            ) : (
              <div className="mt-6 space-y-10" data-testid="folder-contents">
                {shownFolders.map((f) => {
                  const items = samples.filter((s) => s.folder_id === f.id);
                  return (
                    <section key={f.id} data-testid={`folder-section-${slug(f.name)}`}>
                      <div className="flex items-center gap-2">
                        <Folder size={18} className="text-[#1B66C9]" />
                        <h2 className="font-display text-xl font-black" data-testid={`folder-section-${slug(f.name)}-name`}>{f.name}</h2>
                        <span className="text-sm text-muted-foreground">· {items.length} item{items.length === 1 ? "" : "s"}</span>
                      </div>
                      <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid={`folder-section-${slug(f.name)}-grid`}>
                        {items.map((s) => (
                          <SampleTile
                            key={s.id}
                            item={s}
                            testId={`sample-${s.id}`}
                            checked={false}
                            onCheck={() => {}}
                            hideCheck
                            onOpen={() => setViewer({ items: shownItems, index: shownItems.findIndex((x) => x.id === s.id) })}
                          />
                        ))}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </>
        )}
      </main>
      {viewer && <SampleViewer items={viewer.items} index={viewer.index} onIndex={(i) => setViewer({ ...viewer, index: i })} onClose={() => setViewer(null)} />}
    </div>
  );
}
